import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { MiniTable } from '@rr/ui';
import { Badge } from '@mantine/core';

import { GET_STUDENT_TEACHERS_QUERY } from './GetStudentTeachers.query.gql';
import { GetStudentDetailTeachersQuery, Users } from 'types/graphql';

interface NameCell {
  value: string;
  cell: {
    row: {
      original: Users;
    };
  };
}

interface LocationNameCell {
  value: string;
  cell: {
    row: {
      original: {
        locationId: string;
      };
    };
  };
}

interface InstrumentNameCell {
  value: string;
  cell: {
    row: {
      original: {
        instrumentId: string;
      };
    };
  };
}

export const StudentTeachers = () => {
  const { studentId }: { studentId: string } = useParams();
  const [offset, setOffset] = useState(0);
  const LIMIT = 5;

  useEffect(() => {
    setOffset(0);
  }, [studentId]);

  const updateOffset = (number: number) => setOffset(number);

  const { loading, error, data } = useQuery<GetStudentDetailTeachersQuery>(
    GET_STUDENT_TEACHERS_QUERY,
    {
      variables: {
        id: studentId,
        offset,
        limit: LIMIT,
      },
    }
  );

  if (error) {
    console.error(error);
    return <div>Error</div>;
  }

  const TEACHER_COLUMNS = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: ({ value, cell }: NameCell) => (
        <Link to={`/teachers/${cell.row.original.id}`}>{value}</Link>
      ),
    },
    {
      Header: 'Location',
      accessor: 'location',
      Cell: ({ value, cell }: LocationNameCell) =>
        value ? (
          <Link to={`/locations/${cell.row.original.locationId}`}>{value}</Link>
        ) : (
          <Badge color="red" variant="filled">
            location missing
          </Badge>
        ),
    },
    {
      Header: 'Instrument',
      accessor: 'instrument',
      Cell: ({ value, cell }: InstrumentNameCell) =>
        value ? (
          <Link to={`/instruments/${cell.row.original.instrumentId}`}>
            {value}
          </Link>
        ) : (
          <Badge color="red" variant="filled">
            instrument missing
          </Badge>
        ),
    },
  ];

  const teacherData =
    data?.students_by_pk?.student_teachers.map(({ user }) => ({
      id: user.id,
      name: user.gdpr_name,
      location: user.teacher_locations[0]?.location?.name,
      instrument: user.teacher_instruments[0]?.instrument?.prettyName,
    })) || [];

  return teacherData.length !== 0 ? (
    <MiniTable.Wrapper
      columns={TEACHER_COLUMNS}
      offset={offset}
      count={data?.students_by_pk?.student_teachers_aggregate.aggregate?.count}
      limit={LIMIT}
      updateOffset={updateOffset}
      data={teacherData}
    >
      <MiniTable.Header />
      <MiniTable.Body />
      <MiniTable.Pagination />
    </MiniTable.Wrapper>
  ) : (
    <MiniTable.Zero>No Teachers</MiniTable.Zero>
  );
};
